import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

/**
 * useNotificationUnreadCount — backs the sidebar Notificaciones badge.
 *
 * Sprint 1 B1 (REQ-2). Reads `GET /api/v1/notifications/unread-count`
 * (added by WU-3), which returns `{ count: number }`. The hook is
 * intentionally narrow:
 *
 *   - `staleTime: 60s` — the badge reflects "is there anything
 *     new" at a glance; the user does not need millisecond fidelity.
 *   - `refetchInterval: 60s` — keeps the badge fresh without SSE
 *     overhead (the SSE feed already exists for instant delivery).
 *   - `enabled` driven by the user's `notifications.view` permission
 *     so users without the perm never pay the round-trip.
 *
 * Returns `null` (not 0) while loading so the sidebar can hide the
 * pill entirely during the first paint instead of flashing a `0`.
 */

interface UnreadCountResponse {
  count: number;
}

export interface UseNotificationUnreadCountOptions {
  /** When false, the query is paused and `data === undefined`. */
  enabled: boolean;
}

export function useNotificationUnreadCount(options: UseNotificationUnreadCountOptions): {
  count: number | null;
  isLoading: boolean;
  isError: boolean;
} {
  const { enabled } = options;
  const query = useQuery<UnreadCountResponse>({
    queryKey: ['notifications', 'unread-count'] as const,
    queryFn: () =>
      apiFetch<UnreadCountResponse>('/notifications/unread-count', {
        skipAuthRedirect: true,
      }),
    enabled,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  return {
    count: query.data?.count ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

// Helper exposed for unit tests — keeps the formatter co-located
// with the sidebar concern it serves. Pure function, no React.
export function formatBadge(count: number | null | undefined): string | null {
  if (count == null || count <= 0) return null;
  if (count >= 100) return '99+';
  return String(count);
}
